/**
 * ProjetoBoard — nível 2 do módulo: o Kanban de TAREFAS de um projeto.
 *
 * Colunas = projetos.stages do pipeline do projeto. Cards = tasks.
 * Arrastar um card entre colunas grava pjMoveTask (troca stage + log + status
 * segue is_ganho da coluna). Clicar num card abre o TaskPanel (popup) com
 * descrição, responsáveis, datas, prioridade, checklist, comentários, anexos e
 * histórico.
 *
 * A camada visual reproduz o conceito
 * `integracao-nova/projetos/titam-crm-projetos-conceito-v1.png`: breadcrumb +
 * título/badges/ações, faixa de indicadores (anel de progresso, tarefas, atraso,
 * prazo, facepile), abas de visão (Quadro/Lista/Cronograma) com filtro e busca,
 * e o quadro de colunas com acento por coluna. Estilos em `projetos.css`
 * (prefixo pj-) — próprios do módulo, sem reusar as classes kbc-/kanban- do
 * Comercial.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  pjGetBoard, pjCreateTask, pjMoveTask, pjUpdateProject, pjDeleteProject,
  pjCreateStage, pjUpdateStage, pjDeleteStage, getCrmMembers,
} from '../../db/api.js'
import {
  PRIO_BADGE, PRIO_LABEL, PROJECT_STATUS_COLORS, PROJECT_STATUS_LABEL,
  COL_ACCENTS, COL_ACCENT_WON,
  initials, fmtDate, fmtDateShort, isOverdue, pctDone,
} from './projetosKit.jsx'
import TaskPanel from './TaskPanel.jsx'
import ClientePicker from './ClientePicker.jsx'
import ProjectMenu from './ProjectMenu.jsx'
import EditProjectModal from './EditProjectModal.jsx'
import SaveAsTemplateModal from './SaveAsTemplateModal.jsx'
import { ACTIVITY_TYPE_ICONS, ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
import './projetos.css'

const VIEWS = [
  { id: 'quadro', label: 'Quadro', icon: 'columns' },
  { id: 'lista', label: 'Lista', icon: 'timeline' },
  { id: 'cronograma', label: 'Cronograma', icon: 'calendar' },
]

// Status do projeto → pílula tintada do cabeçalho (verde "Em andamento" no
// conceito). Deriva o fundo/texto da cor de status já usada no módulo.
const STATUS_TINT = {
  ativo: { bg: '#e7f7ee', fg: '#12805c', dot: '#12805c' },
  pausado: { bg: '#fff4e5', fg: '#b54708', dot: '#f79009' },
  concluido: { bg: '#e7f7ee', fg: '#12805c', dot: '#12805c' },
  arquivado: { bg: 'var(--bg,#f2f4f7)', fg: 'var(--sec,#667085)', dot: 'var(--ter,#98a2b3)' },
}

function BoardFrame({ embedded, children, onKeyDown }) {
  return embedded ? <div className="pj-embedded" onKeyDown={onKeyDown}>{children}</div> : <Layout title="Projetos">{children}</Layout>
}

export default function ProjetoBoard({ projectId: selectedProjectId, embedded = false, onClose } = {}) {
  const { id: routeProjectId } = useParams()
  const projectId = selectedProjectId || routeProjectId
  const navigate = useNavigate()
  const { selectedClient, confirm, toast } = useApp()
  const clientId = selectedClient?.id || null

  const [board, setBoard] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [dragId, setDragId] = useState(null)
  const [dropCol, setDropCol] = useState(null)
  const [openTaskId, setOpenTaskId] = useState(null)
  const [addingCol, setAddingCol] = useState(null)   // stage_id em quick-add
  const [addTitle, setAddTitle] = useState('')
  const [editCliente, setEditCliente] = useState(false)
  const [editingProject, setEditingProject] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [view, setView] = useState('quadro')
  const [taskSearch, setTaskSearch] = useState('')
  const [taskStatus, setTaskStatus] = useState('all')
  // colunas (stages do pipeline do projeto)
  const [renamingCol, setRenamingCol] = useState(null)
  const [renameVal, setRenameVal] = useState('')
  const [addingColumn, setAddingColumn] = useState(false)
  const [addColName, setAddColName] = useState('')
  const [dragCol, setDragCol] = useState(null)   // coluna sendo arrastada (reorder)
  const [colOver, setColOver] = useState(null)

  const load = async () => {
    try { setBoard(await pjGetBoard(projectId)) }
    catch (e) { setErr(e.message) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    setBoard(null); setErr(null); setLoading(true)
    if (!clientId) { setLoading(false); return }
    let alive = true
    ;(async () => {
      try { const b = await pjGetBoard(projectId); if (alive) setBoard(b) }
      catch (e) { if (alive) setErr(e.message) }
      finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false }
  }, [projectId, clientId])

  // Membros da organização — o board só devolve assignee_ids (uuid); os nomes
  // p/ avatar/facepile vêm daqui (mesma fonte que o TaskPanel usa).
  useEffect(() => { getCrmMembers().then(setMembers).catch(() => {}) }, [clientId])
  const memberName = (uid) => {
    const m = members.find((x) => x.id === uid)
    return m?.nome || m?.email || uid
  }

  const project = board?.project
  const columns = board?.columns || []
  const realCols = columns.filter((c) => c.id != null)

  const allTasks = useMemo(() => columns.flatMap((c) => c.tasks || []), [columns])
  const tasksTotal = allTasks.length
  const tasksDone = allTasks.filter((t) => t.status === 'concluido').length
  const tasksOverdue = allTasks.filter(isOverdue).length
  const pct = pctDone(tasksDone, tasksTotal)
  const memberIds = useMemo(() => {
    const set = new Set()
    allTasks.forEach((t) => (t.assignee_ids || []).forEach((id) => set.add(id)))
    return [...set]
  }, [allTasks])

  const q = taskSearch.trim().toLowerCase()
  const visibleColumns = useMemo(() => {
    return columns.map((c) => ({ ...c, tasks: (c.tasks || []).filter((t) =>
      t.titulo.toLowerCase().includes(q) && (taskStatus === 'all' ||
        (taskStatus === 'open' && t.status !== 'concluido') ||
        (taskStatus === 'completed' && t.status === 'concluido') ||
        (taskStatus === 'overdue' && isOverdue(t)))) }))
  }, [columns, q, taskStatus])
  const visibleTotal = visibleColumns.reduce((sum, col) => sum + col.tasks.length, 0)

  const handleDrop = async (stageId) => {
    setDropCol(null)
    const tid = dragId
    setDragId(null)
    if (!tid || stageId == null) return
    // otimista: move o card já e reverte no erro
    setBoard((prev) => {
      if (!prev) return prev
      const cols = prev.columns.map((c) => ({ ...c, tasks: c.tasks.filter((t) => t.id !== tid) }))
      let moved
      for (const c of prev.columns) { const f = c.tasks.find((t) => t.id === tid); if (f) moved = f }
      const target = cols.find((c) => c.id === stageId)
      if (moved && target) target.tasks = [...target.tasks, { ...moved, stage_id: stageId }]
      return { ...prev, columns: cols }
    })
    try { await pjMoveTask(tid, { toStageId: stageId }) }
    catch (e) { toast(`Erro ao mover: ${e.message}`, 'error'); load() }
  }

  const linkCliente = async (c) => {
    if (!c) return           // desvincular não é suportado no PATCH (null é ignorado)
    try {
      await pjUpdateProject(projectId, { contact_id: c.id })
      setEditCliente(false)
      load()
    } catch (e) { toast(e.message, 'error') }
  }

  const removeProject = async () => {
    const ok = await confirm({
      title: 'Excluir projeto',
      message: `Excluir o projeto "${project?.nome}"? As tarefas vinculadas também serão removidas.`,
      confirmText: 'Excluir projeto', danger: true,
    })
    if (!ok) return
    try { await pjDeleteProject(projectId); if (embedded) onClose?.(); else navigate('/projetos') }
    catch (e) { toast(`Erro ao excluir: ${e.message}`, 'error') }
  }

  const quickAdd = async (stageId) => {
    if (!addTitle.trim()) { setAddingCol(null); return }
    try {
      await pjCreateTask(projectId, { titulo: addTitle.trim(), stage_id: stageId })
      setAddTitle(''); setAddingCol(null)
      load()
    } catch (e) { toast(e.message, 'error') }
  }

  const novaTarefa = () => {
    if (view !== 'quadro') setView('quadro')
    const first = realCols[0]
    if (!first) { toast('Crie uma coluna antes de adicionar tarefas.', 'error'); return }
    setAddingCol(first.id); setAddTitle('')
  }

  const addColumn = async () => {
    const nome = addColName.trim()
    if (!nome) { setAddingColumn(false); return }
    if (!project?.pipeline_id) { toast('Projeto sem quadro.', 'error'); return }
    try {
      await pjCreateStage(project.id, { nome, ordem: realCols.length })
      setAddColName(''); setAddingColumn(false); load()
    } catch (e) { toast(e.message, 'error') }
  }

  const renameColumn = async (col) => {
    const nome = renameVal.trim()
    if (!nome || nome === col.nome) { setRenamingCol(null); return }
    try { await pjUpdateStage(col.id, { nome }); setRenamingCol(null); load() }
    catch (e) { toast(e.message, 'error') }
  }

  const deleteColumn = async (col) => {
    const n = col.tasks.length
    const ok = await confirm({
      title: 'Excluir coluna',
      message: n > 0
        ? `A coluna "${col.nome}" tem ${n} tarefa(s). Ao excluir, elas ficarão sem coluna (você poderá movê-las depois). Continuar?`
        : `Excluir a coluna "${col.nome}"?`,
      confirmText: 'Excluir coluna', danger: true,
    })
    if (!ok) return
    try { await pjDeleteStage(col.id); load() } catch (e) { toast(e.message, 'error') }
  }

  const reorderColumns = async (targetId) => {
    const src = dragCol
    setDragCol(null); setColOver(null)
    if (!src || src === targetId) return
    const arr = realCols.map((c) => c.id)
    const from = arr.indexOf(src)
    if (from < 0) return
    arr.splice(from, 1)
    let ti = arr.indexOf(targetId)
    if (ti < 0) ti = arr.length
    arr.splice(ti, 0, src)
    // otimista: reordena as colunas na tela; persiste ordem no backend
    setBoard((prev) => {
      if (!prev) return prev
      const byId = Object.fromEntries(prev.columns.map((c) => [c.id, c]))
      const virt = prev.columns.filter((c) => c.id == null)
      return { ...prev, columns: [...arr.map((id) => byId[id]), ...virt] }
    })
    try { await Promise.all(arr.map((id, i) => pjUpdateStage(id, { ordem: i }))) }
    catch (e) { toast(`Erro ao reordenar: ${e.message}`, 'error'); load() }
  }

  if (!clientId) {
    return (
      <BoardFrame embedded={embedded}>
        <div className="pj"><div className="pj-empty">Selecione um cliente.</div></div>
      </BoardFrame>
    )
  }

  const tint = project ? (STATUS_TINT[project.status] || STATUS_TINT.arquivado) : STATUS_TINT.arquivado
  const statusColor = project ? (PROJECT_STATUS_COLORS[project.status] || 'var(--ter,#98a2b3)') : 'var(--ter,#98a2b3)'
  const origemLabel = project?.deal_titulo || project?.company_nome

  return (
    <BoardFrame embedded={embedded} onKeyDown={e => {
      // O popup externo não deve fechar enquanto um editor interno estiver aberto.
      if (e.key === 'Escape' && (openTaskId || editingProject || savingTemplate || addingCol || renamingCol || addingColumn)) e.stopPropagation()
    }}>
      <div className="pj pj-project-board">
        {/* ── Breadcrumb (dentro da página, como no conceito) ────────────── */}
        {!embedded && <nav className="pj-crumb">
          <Link to="/projetos">Projetos</Link>
          <span className="pj-crumb-sep">/</span>
          <span className="pj-crumb-cur">{project?.nome || '…'}</span>
        </nav>}

        {err && <div className="pj-empty" style={{ color: '#b42318', borderColor: '#fbd9d3', background: '#fef6f5' }}>{err}</div>}
        {loading && <div className="pj-empty">Carregando…</div>}

        {!loading && project && (
          <>
            {/* ── Cabeçalho: título + badges + ações ───────────────────────── */}
            <div className="pj-head">
              <div className="pj-head-l">
                <h1 className="pj-title">{project.nome}</h1>
                {origemLabel && (
                  <span className="pj-origin">
                    {origemLabel} <Icon n="external" s={15} sw={2} />
                  </span>
                )}
                <span className="pj-status" style={{ background: tint.bg, color: tint.fg }}>
                  <span className="pj-status-dot" style={{ background: tint.dot || statusColor }} />
                  {PROJECT_STATUS_LABEL[project.status] || project.status}
                </span>
              </div>

              <div className="pj-head-r">
                {editCliente ? (
                  <div style={{ width: 230 }}>
                    <ClientePicker value={null} onChange={linkCliente} placeholder="Vincular cliente…" />
                  </div>
                ) : (
                  <button className="pj-btn pj-btn-ghost" onClick={() => setEditCliente(true)}
                    title={project.contact_nome ? 'Trocar cliente' : 'Vincular cliente'}>
                    <Icon n="settings" s={16} />
                    {project.contact_nome || 'Vincular cliente'}
                  </button>
                )}
                <button className="pj-btn pj-btn-primary" onClick={novaTarefa}>
                  <Icon n="plus" s={16} sw={2.2} /> Nova tarefa
                </button>
                <ProjectMenu
                  bare
                  onEdit={() => setEditingProject(true)}
                  onSaveTemplate={() => setSavingTemplate(true)}
                  onDelete={removeProject}
                />
              </div>
            </div>

            {/* ── Indicadores: progresso, tarefas, atraso, prazo, membros ──── */}
            <div className="pj-stats">
              <div className="pj-stat">
                <ProgressRing pct={pct} />
                <div className="pj-stat-txt">
                  <span className="pj-stat-label">Progresso</span>
                  <span className="pj-stat-value is-blue">{pct}%</span>
                </div>
              </div>
              <div className="pj-stat-sep" />
              <div className="pj-stat">
                <div className="pj-stat-txt">
                  <span className="pj-stat-value">{tasksTotal}</span>
                  <span className="pj-stat-label">tarefas</span>
                </div>
              </div>
              <div className="pj-stat-sep" />
              <div className="pj-stat">
                <div className="pj-stat-txt">
                  <span className={`pj-stat-value${tasksOverdue > 0 ? ' is-amber' : ''}`}>{tasksOverdue}</span>
                  <span className="pj-stat-label">em atraso</span>
                </div>
              </div>
              <div className="pj-stat-sep" />
              <div className="pj-stat">
                <div className="pj-stat-txt">
                  <span className="pj-stat-label">Prazo</span>
                  <span className="pj-stat-value is-date">{fmtDateShort(project.prazo) || fmtDate(project.prazo) || '—'}</span>
                </div>
              </div>
              <div className="pj-stat pj-stat-members">
                <span className="pj-stat-label">Membros</span>
                {memberIds.length > 0 ? (
                  <div className="pj-avatars">
                    {memberIds.slice(0, 4).map((id) => (
                      <span key={id} className="pj-av pj-av-lg" title={memberName(id)}>
                        {initials(memberName(id))}
                      </span>
                    ))}
                    {memberIds.length > 4 && <span className="pj-av-more">+{memberIds.length - 4}</span>}
                  </div>
                ) : <span className="pj-stat-label">—</span>}
              </div>
            </div>

            {/* ── Abas de visão + filtro + busca ───────────────────────────── */}
            <div className="pj-tools">
              <div className="pj-views">
                {VIEWS.map((v) => (
                  <button key={v.id} aria-pressed={view === v.id} className={`pj-view${view === v.id ? ' is-active' : ''}`}
                    onClick={() => setView(v.id)}>
                    <Icon n={v.icon} s={17} /> {v.label}
                  </button>
                ))}
              </div>
              <div className="pj-tools-r">
                <select className="pj-board-status-filter" aria-label="Filtrar tarefas do projeto por status" value={taskStatus} onChange={e => setTaskStatus(e.target.value)}>
                  <option value="all">Todas as tarefas</option><option value="open">Abertas</option><option value="completed">Concluídas</option><option value="overdue">Atrasadas</option>
                </select>
                <div className="pj-search">
                  <Icon n="search" s={15} />
                  <input aria-label="Buscar tarefa no projeto" value={taskSearch} placeholder="Buscar tarefa..."
                    onChange={(e) => setTaskSearch(e.target.value)} />
                </div>
              </div>
            </div>

            {view === 'quadro' && (
              <KanbanView
                columns={visibleColumns} realCols={realCols}
                memberName={memberName} openTaskId={openTaskId}
                dragId={dragId} setDragId={setDragId} dropCol={dropCol} setDropCol={setDropCol}
                dragCol={dragCol} setDragCol={setDragCol} colOver={colOver} setColOver={setColOver}
                handleDrop={handleDrop} reorderColumns={reorderColumns}
                addingCol={addingCol} setAddingCol={setAddingCol} addTitle={addTitle} setAddTitle={setAddTitle}
                quickAdd={quickAdd}
                renamingCol={renamingCol} setRenamingCol={setRenamingCol} renameVal={renameVal} setRenameVal={setRenameVal}
                renameColumn={renameColumn} deleteColumn={deleteColumn}
                addingColumn={addingColumn} setAddingColumn={setAddingColumn}
                addColName={addColName} setAddColName={setAddColName} addColumn={addColumn}
                onOpenTask={setOpenTaskId}
              />
            )}

            {view === 'lista' && (
              <ListaView columns={visibleColumns} memberName={memberName} onOpenTask={setOpenTaskId} />
            )}

            {view === 'cronograma' && <CronogramaView />}
            {view !== 'cronograma' && <footer className="pj-board-footer">{visibleTotal} de {tasksTotal} tarefas{(q || taskStatus !== 'all') && <button type="button" onClick={() => { setTaskSearch(''); setTaskStatus('all') }}>Limpar filtros</button>}</footer>}
          </>
        )}
      </div>

      {openTaskId && (
        <TaskPanel
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
          onChanged={load}
        />
      )}

      {editingProject && project && (
        <EditProjectModal
          project={project}
          onClose={() => setEditingProject(false)}
          onSaved={() => { setEditingProject(false); load() }}
        />
      )}

      {savingTemplate && project && (
        <SaveAsTemplateModal
          project={project}
          onClose={() => setSavingTemplate(false)}
          onSaved={() => setSavingTemplate(false)}
        />
      )}
    </BoardFrame>
  )
}

// ── Anel de progresso (SVG) ──────────────────────────────────────────────────
function ProgressRing({ pct }) {
  const r = 24, c = 2 * Math.PI * r
  const off = c - (Math.min(100, Math.max(0, pct)) / 100) * c
  return (
    <svg width={56} height={56} viewBox="0 0 56 56" style={{ flexShrink: 0 }}>
      <circle cx={28} cy={28} r={r} fill="none" stroke="var(--soft,#eaecf0)" strokeWidth={6} />
      <circle cx={28} cy={28} r={r} fill="none" stroke="var(--blue,#2563eb)" strokeWidth={6}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform="rotate(-90 28 28)" style={{ transition: 'stroke-dashoffset .3s' }} />
    </svg>
  )
}

// ── Quadro (Kanban) ──────────────────────────────────────────────────────────
function KanbanView({
  columns, realCols, memberName, openTaskId,
  dragId, setDragId, dropCol, setDropCol, dragCol, setDragCol, colOver, setColOver,
  handleDrop, reorderColumns,
  addingCol, setAddingCol, addTitle, setAddTitle, quickAdd,
  renamingCol, setRenamingCol, renameVal, setRenameVal, renameColumn, deleteColumn,
  addingColumn, setAddingColumn, addColName, setAddColName, addColumn,
  onOpenTask,
}) {
  return (
    <div className="pj-board-scroll">
      <div className="pj-board" style={{ gridTemplateColumns: `${columns.length ? `repeat(${columns.length}, minmax(var(--pj-column-width, 320px), 1fr)) ` : ''}190px` }}>
        {columns.map((col, i) => {
          // 1ª coluna sem acento; ganho sempre verde (com borda no topo).
          const accent = col.is_ganho
            ? COL_ACCENT_WON
            : (col.cor || (i === 0 ? null : COL_ACCENTS[(i - 1) % COL_ACCENTS.length]))
          const isTarget = (dragCol ? colOver : dropCol) === col.id
          return (
            <div key={col.id || 'none'}
              className={`pj-col${isTarget ? ' is-drop' : ''}${col.is_ganho ? ' is-won' : ''}`}
              onDragOver={(e) => {
                e.preventDefault()
                if (dragCol) setColOver(col.id); else setDropCol(col.id)
              }}
              onDragLeave={() => {
                if (dragCol) setColOver((t) => (t === col.id ? null : t))
                else setDropCol((t) => (t === col.id ? null : t))
              }}
              onDrop={() => (dragCol ? reorderColumns(col.id) : handleDrop(col.id))}>

              <div className="pj-col-head"
                draggable={col.id != null && renamingCol !== col.id}
                onDragStart={(e) => {
                  if (col.id == null) return
                  setDragCol(col.id); e.dataTransfer.effectAllowed = 'move'
                }}
                onDragEnd={() => { setDragCol(null); setColOver(null) }}>
                {accent && <span className="pj-col-accent" style={{ background: accent }} />}
                {renamingCol === col.id ? (
                  <input autoFocus className="pj-col-rename" value={renameVal}
                    onChange={(e) => setRenameVal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') renameColumn(col)
                      if (e.key === 'Escape') setRenamingCol(null)
                    }}
                    onBlur={() => renameColumn(col)} />
                ) : (
                  <span className="pj-col-name">{col.nome}</span>
                )}
                <span className="pj-col-count">{col.tasks.length}</span>
                {col.id != null && renamingCol !== col.id && (
                  <ColumnMenu
                    onRename={() => { setRenamingCol(col.id); setRenameVal(col.nome) }}
                    onDelete={() => deleteColumn(col)}
                  />
                )}
              </div>

              <div className="pj-col-body">
                {col.id != null && (addingCol === col.id ? (
                  <input className="pj-inline-input" autoFocus value={addTitle}
                    placeholder="Título da tarefa…"
                    onChange={(e) => setAddTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') quickAdd(col.id)
                      if (e.key === 'Escape') { setAddingCol(null); setAddTitle('') }
                    }}
                    onBlur={() => quickAdd(col.id)} />
                ) : (
                  <button className="pj-add-task"
                    onClick={() => { setAddingCol(col.id); setAddTitle('') }}>
                    <Icon n="plus" s={15} sw={2} /> Adicionar tarefa
                  </button>
                ))}

                {col.tasks.map((t) => (
                  <TaskCard key={t.id} task={t} memberName={memberName}
                    selected={openTaskId === t.id}
                    onOpen={() => onOpenTask(t.id)}
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => { setDragId(null); setDropCol(null) }}
                    dragging={dragId === t.id} />
                ))}
                {!col.tasks.length && <div className="pj-col-empty">Sem tarefas</div>}
              </div>
            </div>
          )
        })}

        {/* Tile de adicionar coluna (fim do quadro) */}
        <div className="pj-add-col">
          {addingColumn ? (
            <input autoFocus className="pj-inline-input" value={addColName}
              placeholder="Nome da coluna…"
              onChange={(e) => setAddColName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addColumn()
                if (e.key === 'Escape') { setAddingColumn(false); setAddColName('') }
              }}
              onBlur={addColumn} />
          ) : (
            <button className="pj-add-col-btn" onClick={() => { setAddingColumn(true); setAddColName('') }}>
              <Icon n="plus" s={16} sw={2} /> Adicionar coluna
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Menu de coluna (kebab → Renomear / Excluir) ──────────────────────────────
function ColumnMenu({ onRename, onDelete }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  const pick = (fn) => { setOpen(false); fn() }
  return (
    <div className={`pj-col-menu${open ? ' is-open' : ''}`} style={{ position: 'relative' }}
      ref={ref} draggable={false} onMouseDown={(e) => e.stopPropagation()}>
      <button className="pj-col-gear" title="Ações da coluna"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}>
        <Icon n="more_v" s={16} />
      </button>
      {open && (
        <div className="pj-menu" style={{ top: '100%', right: 0, marginTop: 4 }} role="menu">
          <button className="pj-menu-item" onClick={() => pick(onRename)}>
            <Icon n="edit" s={14} /> Renomear
          </button>
          <button className="pj-menu-item is-danger" onClick={() => pick(onDelete)}>
            <Icon n="trash" s={14} /> Excluir coluna
          </button>
        </div>
      )}
    </div>
  )
}

// ── Card de tarefa ──────────────────────────────────────────────────────────
function TaskCard({ task, memberName, selected, onOpen, onDragStart, onDragEnd, dragging }) {
  const badge = PRIO_BADGE[task.prioridade] || PRIO_BADGE.media
  const done = task.status === 'concluido'
  const checklistTotal = Number(task.checklist_total) || 0
  const checklistDone = Number(task.checklist_done) || 0
  const checklistPct = checklistTotal ? Math.round((checklistDone / checklistTotal) * 100) : 0
  const assignees = task.assignee_ids || []
  const dateLabel = fmtDateShort(task.prazo)
  const overdue = isOverdue(task)

  return (
    <div className={`pj-card${dragging ? ' is-dragging' : ''}${selected ? ' is-selected' : ''}`} draggable role="button" tabIndex={0} aria-label={`Abrir tarefa ${task.titulo}`}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      onDragStart={(e) => { onDragStart(); e.dataTransfer.effectAllowed = 'move' }}
      onDragEnd={onDragEnd}>
      <div className="pj-board-card-top"><span><Icon n={ACTIVITY_TYPE_ICONS[task.tipo] || 'page'} s={13}/>{ACTIVITY_TYPE_LABELS[task.tipo] || 'Tarefa'}</span><span className="pj-prio" style={{ background: badge.bg, color: badge.fg }}>{PRIO_LABEL[task.prioridade] || task.prioridade}</span></div>
      <div className="pj-card-title">
        {done && (
          <span className="pj-card-check"><Icon n="check" s={12} sw={3} c="#fff" /></span>
        )}
        <span>{task.titulo}</span>
      </div>

      {assignees.length > 0 ? (
        <div className="pj-card-who">
          <span className="pj-av pj-av-sm">{initials(memberName(assignees[0]))}</span>
          <span className="pj-card-who-name">{memberName(assignees[0])}</span>
          {assignees.length > 1 && <span className="pj-meta" title={assignees.slice(1).map(memberName).join(', ')}>+{assignees.length - 1}</span>}
        </div>
      ) : <span className="pj-board-unassigned"><Icon n="user" s={14}/>Sem responsável</span>}

      <div className="pj-card-meta">
        <span className={`pj-meta${overdue ? ' is-late' : ''}`}>
          <Icon n="calendar" s={15} /> {dateLabel || 'Sem prazo'}
        </span>
        <span className="pj-meta">
          <Icon n="attend" s={15} /> {Number(task.comments_count) || 0}
        </span>
      </div>

      {checklistTotal > 0 && (
        <div className="pj-card-foot">
          <span className="pj-frac" title="Itens do checklist concluídos">{checklistDone}/{checklistTotal}</span>
          <div className="pj-track">
            <div className="pj-track-fill" style={{ width: `${checklistPct}%` }} />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Lista (tabela plana de tarefas do projeto) ───────────────────────────────
function ListaView({ columns, memberName, onOpenTask }) {
  const rows = useMemo(() => columns.flatMap(c => (c.tasks || []).map(t => ({ ...t, colNome: c.nome, colCor: c.cor }))), [columns])
  return <div className="pj-task-table-scroll">
    <table className="pj-task-table" aria-label="Tarefas do projeto">
      <colgroup><col style={{width:'38%'}}/><col style={{width:'13%'}}/><col style={{width:'14%'}}/><col style={{width:'10%'}}/><col style={{width:'16%'}}/><col style={{width:'9%'}}/></colgroup>
      <thead><tr><th scope="col">Tarefa</th><th scope="col">Prazo</th><th scope="col">Etapa</th><th scope="col">Prioridade</th><th scope="col">Responsáveis</th><th scope="col">Status</th></tr></thead>
      <tbody>{rows.length ? rows.map(task => {
        const done = task.status === 'concluido'
        const overdue = isOverdue(task)
        const owners = task.assignee_ids || []
        return <tr key={task.id} onClick={() => onOpenTask(task.id)}>
          <td><button type="button" className="pj-table-task" onClick={e => { e.stopPropagation(); onOpenTask(task.id) }}>
            <Icon n={ACTIVITY_TYPE_ICONS[task.tipo] || 'page'} s={16}/><span>{task.titulo}</span>
          </button></td>
          <td><span className={`pj-table-date${overdue ? ' is-late' : ''}`}><Icon n="calendar" s={14}/>{task.prazo ? fmtDate(task.prazo) : 'Sem prazo'}</span></td>
          <td><span className="pj-table-stage"><i style={{background:task.colCor || '#98a2b3'}}/>{task.colNome}</span></td>
          <td><span className={`pj-table-priority is-${task.prioridade || 'media'}`}>{['alta', 'urgente', 'baixa'].includes(task.prioridade) ? <Icon n={task.prioridade === 'baixa' ? 'arrow_down' : 'arrow_up'} s={13}/> : <span>−</span>}{PRIO_LABEL[task.prioridade] || task.prioridade}</span></td>
          <td><div className="pj-table-owners">{owners.length ? owners.map(id => <span className="pj-card-who" key={id}><span className="pj-av pj-av-sm">{initials(memberName(id))}</span><span className="pj-card-who-name">{memberName(id)}</span></span>) : <span className="pj-board-unassigned">Sem responsável</span>}</div></td>
          <td><span className={`pj-table-status${done ? ' is-done' : overdue ? ' is-late' : ''}`}><Icon n={done ? 'check' : 'clock'} s={13}/>{done ? 'Concluída' : overdue ? 'Atrasada' : 'Aberta'}</span></td>
        </tr>
      }) : <tr><td colSpan={6} className="pj-table-empty">Nenhuma tarefa encontrada para estes filtros.</td></tr>}</tbody>
    </table>
  </div>
}

// ── Cronograma (placeholder — sem dado de série temporal ainda) ─────────────
function CronogramaView() {
  return (
    <div className="pj-empty">
      <Icon n="calendar" s={28} c="var(--ter,#98a2b3)" />
      <div>Cronograma chega em breve.</div>
      <div className="pj-empty-hint">
        Uma visão de linha do tempo das tarefas por prazo, ao lado do Quadro e da Lista.
      </div>
    </div>
  )
}
